// Replacement or refund on the step (2026-10-27, docs/contact-view.md
// "Replacement or refund on the step"; @cxhub/shared's resolution-switch.js).
// Pure helpers for the agent draft panel and the admin agent column, tested
// in ui/test/resolutionSwitch.test.js.

import { isResolutionChoice, isResolutionReplyStep, outOfStockLine, RESOLUTION_LABELS, RESOLUTION_REFUND, RESOLUTION_SPLIT, RESOLUTION_WHY_OUT_OF_STOCK, RESOLUTION_WHY_CSR_CHOICE } from '@cxhub/shared'

// The choice step and the paths' reply steps are never shown as steps: the
// choice shows on the head step, and a path's reply loads into the box.
export function isResolutionHiddenStep(a) {
  return isResolutionChoice(a) || isResolutionReplyStep(a)
}

export function resolutionChoiceIndex(actions = []) {
  return (actions ?? []).findIndex((a) => isResolutionChoice(a))
}

// The first shown step of a path: where the choice is drawn.
export function resolutionHeadIndex(actions = [], path = null) {
  return (actions ?? []).findIndex((a) => a?.taskPath === path && !isResolutionHiddenStep(a) && a?.type !== 'select_reason_code')
}

// The out-of-stock lines of a replacement step (params.outOfStock).
export function stockWarningLines(step) {
  return (step?.params?.outOfStock ?? []).map(outOfStockLine)
}

// What the head step shows for the path in effect, or null without a
// resolution choice.
export function resolutionView(actions = [], current = null) {
  const index = resolutionChoiceIndex(actions)
  if (index < 0) return null
  const choice = actions[index]
  const p = choice.params ?? {}
  const preselected = p.preselected?.value ?? null
  const path = current ?? preselected
  return {
    index,
    path,
    headPath: preselected,
    headIndex: resolutionHeadIndex(actions, path),
    options: p.options ?? [],
    toggle: p.toggle === true,
    customerChoice: p.customerChoice ?? 'unspecified',
    customerQuote: p.customerQuote ?? null,
    stockLines: (p.outOfStock ?? []).map(outOfStockLine),
    // Outside the return window (2026-10-27): the note on the step.
    note: p.windowNote ?? null,
    notes: p.notes ?? {},
  }
}

// Why a switch: out of stock when the step had an out-of-stock line and the
// switch moves off the replacement; else the person's own choice.
export function resolutionWhyFor({ stockLines = [], to }) {
  return stockLines.length && (to === RESOLUTION_REFUND || to === RESOLUTION_SPLIT) ? RESOLUTION_WHY_OUT_OF_STOCK : RESOLUTION_WHY_CSR_CHOICE
}

// The order note step both paths share (an add_order_note with no path).
export function resolutionNoteIndex(actions = []) {
  return (actions ?? []).findIndex((a) => a?.type === 'add_order_note' && !a.taskPath)
}

// What a switch does to the reply and the note.
//   reply: the text the box takes ({ text, title }), from the path's own
//     earlier text (the person's edits, replyByPath) or its reply step; null
//     when the path has none (the box is left).
//   askReply: the person edited the reply, so the panel asks first ("Replace
//     your reply?": Keep my reply / Use the new template).
//   note: the note box's new text, or null to leave it: the path's default
//     note, unless the person edited the note (their text is kept).
export function resolutionSwitchPlan({ actions = [], from, to, draftDirty = false, replyByPath = {}, noteInput = null, noteBaseline = null } = {}) {
  const view = resolutionView(actions, from)
  const replyStep = (actions ?? []).find((a) => isResolutionReplyStep(a) && a.params.resolutionPath === to) ?? null
  const own = replyByPath?.[to]
  const text = typeof own === 'string' ? own : (replyStep?.params?.text ?? '')
  const reply = String(text ?? '').trim() ? { text, title: replyStep?.params?.templateTitle ?? null, own: typeof own === 'string' } : null
  const noteEdited = noteInput != null && noteInput !== noteBaseline
  const nextNote = view?.notes?.[to] ?? null
  return {
    reply,
    askReply: Boolean(reply) && draftDirty,
    note: !noteEdited && nextNote ? nextNote : null,
  }
}

export function resolutionLabel(path) {
  return RESOLUTION_LABELS[path] ?? path
}

// The admin agent column (2026-10-27): which previews show, numbered as
// shown, the bar on each group's first shown step, the hidden indexes, and
// the reply of a switched path. previews: the draft's stepPreviews (each
// group's head carries `resolution`); paths: { [group]: path } chosen here.
// { shown: [{ preview, index, number, bar }], hiddenIndexes, reply }.
// decisions (2026-10-28): { [decisionGroup]: 'approve' | 'reject' } chosen
// here for an eBay cancel request; only the decision's pair of steps shows
// (shadowDecisionOf()).
export function shadowResolutionState(previews = [], paths = {}, decisions = {}) {
  const heads = new Map((previews ?? []).filter((p) => p?.resolution).map((p) => [p.resolution.group, p]))
  const currentOf = (group) => paths?.[group] ?? heads.get(group)?.resolution?.current ?? null
  const shown = []
  const hiddenIndexes = []
  const barDone = new Set()
  let reply = null
  ;(previews ?? []).forEach((p, index) => {
    if (p?.decisionGroup && p.decisionPath !== shadowDecisionOf(previews, p.decisionGroup, decisions)) {
      hiddenIndexes.push(index)
      return
    }
    const group = p?.resolutionGroup ?? null
    if (group && heads.has(group) && p.resolutionPath !== currentOf(group)) {
      hiddenIndexes.push(index)
      return
    }
    let bar = null
    if (group && heads.has(group) && !barDone.has(group)) {
      barDone.add(group)
      const r = heads.get(group).resolution
      const path = currentOf(group)
      bar = { group, path, headPath: r.headPath, options: r.options ?? [], toggle: r.toggle === true, stockLines: r.stockLines ?? [], customerQuote: r.customerQuote ?? null, note: r.note ?? null }
      if (bar.options.length < 2 && !bar.stockLines.length && !bar.note) bar = null
      if (path !== r.headPath && r.replies?.[path]?.text && reply == null) reply = r.replies[path].text
    }
    shown.push({ preview: p, index, number: shown.length + 1, bar })
  })
  return { shown, hiddenIndexes, reply }
}

// An eBay cancel request's decision in effect on the admin agent column
// (2026-10-28): the one chosen here, else the server's (the eBay step's
// value).
export function shadowDecisionOf(previews = [], group, decisions = {}) {
  if (decisions?.[group]) return decisions[group]
  const step = (previews ?? []).find((p) => p?.decisionGroup === group && p?.values?.decision)
  if (step) return step.values.decision
  return (previews ?? []).find((p) => p?.decisionGroup === group && p.active !== false)?.decisionPath ?? null
}
